import config from "../config/config";
import { Errors } from "../constants/errors";
import { ERROR_SELECTOR_TO_NAME } from "./viem/commonErrors";
import { toastError } from "../utils/utils";

type ErrorTemplate = { code: string; message: string };

export type ParsedTxError = ErrorTemplate & {
  kind:
    | "rejected"
    | "indexing"
    | "insufficient-funds"
    | "reverted"
    | "tx-reverted"
    | "network"
    | "wrong-network"
    | "nonce"
    | "out-of-gas"
    | "unknown";
  params?: { [key: string]: string };
};

const CONTRACT_ERROR_MESSAGES: Record<string, string> = {
  AuctionEnded: "This auction has already ended.",
  AuctionNotEnded: "This auction has not ended yet.",
  BuyCooldownNotReset: "Buy cooldown has not reset yet. Please wait.",
  CarNotInSameCity: "That car is not in the same city.",
  CarNotOwned: "You do not own that car.",
  CommonMissionNotCompleted: "Complete the common mission first.",
  ContractNotAllowed: "This action cannot be done from a contract.",
  ERC20InsufficientAllowance:
    "Token approval is too low. Approve the token and try again.",
  ERC20InsufficientBalance: "Not enough token balance for this action.",
  ERC1155InsufficientBalance: "Not enough NFT keys or items for this action.",
  ERC1155MissingApprovalForAll:
    "NFT approval is missing. Approve the collection and try again.",
  EnforcedPause: "This contract is currently paused.",
  HealthOverflow: "That would exceed the maximum health.",
  InvalidBid: "That bid is invalid.",
  InvalidCashAmountForPrizeType: "Cash amount does not match the prize type.",
  InvalidDuration: "That duration is invalid.",
  InvalidListingToken: "That listing token is invalid.",
  InvalidListingType: "That listing type is invalid.",
  InvalidPrice: "That price is invalid.",
  InvalidStartPrice: "That starting price is invalid.",
  InvalidSwapToken: "That swap token is invalid.",
  InvalidXpType: "That XP type is invalid.",
  ItemNotActive: "That item is not active.",
  NoBids: "There are no bids on this listing.",
  NoMysteryBoxFound: "You don't have a mystery box to open.",
  NotACarItem: "That item is not a car.",
  NotAuthorized: "You are not authorized to do this.",
  NotEnoughHealth: "You do not have enough health.",
  NotEnoughHealthLeft: "There is not enough health left.",
  NotHospitalOwner: "Only the hospital owner can do this.",
  NotInSameCity: "You are not in the same city.",
  NotOwner: "Only the owner can do this.",
  NotRaceCreator: "Only the race creator can do this.",
  NotValidCity: "That city is not valid for this action.",
  OwnableUnauthorizedAccount: "Only the contract owner can do this.",
  PerkActiveBlockingListing:
    "An active perk is blocking this listing. Cancel it first.",
  PlayerAlreadyHasActiveLobby: "You already have an active lobby.",
  PlayerCannotJoinOwnRace: "You cannot join your own race.",
  RaceAlreadyJoined: "You have already joined this race.",
  RaceCooldown: "Race cooldown is still active. Please wait.",
  RaceDoesNotExist: "That race does not exist.",
  RaceNotPending: "That race is not waiting for players.",
  RaceNotStarted: "That race has not started yet.",
  ShouldReproduceHealth: "Health needs to be reproduced first.",
  SmuggleMissionNotCompleted: "Complete the smuggle mission first.",
  StartPriceMustBeGreaterThanZero: "Start price must be greater than zero.",
  TransferHelper: "Token transfer failed. Check your balance and approval.",
  UserInJail: "You can't do this while in jail.",
};

const ERC_REQUIRE_MESSAGES: Array<{ test: RegExp; message: string }> = [
  {
    test: /erc20: transfer amount exceeds balance|transfer amount exceeds balance/i,
    message: "Not enough token balance for this action.",
  },
  {
    test: /erc20: insufficient allowance|insufficient allowance/i,
    message: "Token approval is too low. Approve the token and try again.",
  },
  {
    test: /erc1155:.*insufficient|insufficient balance for transfer/i,
    message: "Not enough NFT keys or items for this action.",
  },
  {
    test: /erc1155: caller is not token owner or approved|caller is not owner nor approved/i,
    message: "You are not approved to move this NFT. Approve it and try again.",
  },
  {
    test: /transferhelper|transfer_failed/i,
    message: "Token transfer failed. Check your balance and approval.",
  },
];

const collectErrorText = (error: unknown): string => {
  const parts: string[] = [];
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number) => {
    if (value == null || depth > 5 || seen.has(value)) return;
    if (typeof value === "string" || typeof value === "number") {
      parts.push(String(value));
      return;
    }
    if (typeof value !== "object") return;
    seen.add(value);

    const obj = value as Record<string, unknown>;
    for (const key of [
      "name",
      "shortMessage",
      "message",
      "details",
      "reason",
      "errorName",
      "code",
    ]) {
      if (typeof obj[key] === "string" && obj[key]) {
        parts.push(obj[key] as string);
      }
    }
    visit(obj.cause, depth + 1);
    visit(obj.error, depth + 1);
    visit(obj.data, depth + 1);
  };

  visit(error, 0);
  return parts.join("\n");
};

const matches = (text: string, pattern: RegExp) => pattern.test(text);

const humanizeErrorName = (name: string): string => {
  const spaced = name
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return spaced;
};

const extractCustomErrorName = (text: string): string | null => {
  const patterns = [
    /\bError:\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:\(|$)/m,
    /\breverted with custom error\s+['"]?([A-Za-z_][A-Za-z0-9_]*)/i,
    /\bexecution reverted:\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:\(|$)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1] && match[1] !== "Error") return match[1];
  }
  return null;
};

const isCustomErrorCode = (code: string) =>
  code !== "Error" &&
  code !== "Panic" &&
  (CONTRACT_ERROR_MESSAGES[code] != null || /^[A-Z][A-Za-z0-9]+$/.test(code));

const extractErrorSelector = (text: string): string | null => {
  const patterns = [
    /unable to decode signature ['"]?(0x[0-9a-f]{8})['"]?/i,
    /reverted with the following signature:\s*(0x[0-9a-f]{8})\b/i,
    /custom error (0x[0-9a-f]{8})\b/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].toLowerCase();
  }

  const tokens = text.match(/0x[0-9a-f]{8}(?![0-9a-f])/gi) ?? [];
  for (const token of tokens) {
    const selector = token.toLowerCase();
    if (ERROR_SELECTOR_TO_NAME[selector]) return selector;
  }
  return null;
};

const extractRevertReason = (text: string): string | null => {
  const patterns = [
    /reverted with the following reason:\s*\n?\s*(.+)/i,
    /reverted with reason string ['"]([^'"]+)['"]/i,
    /execution reverted:\s*(.+)/i,
    /^reason:\s*(.+)/im,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    const raw = match?.[1]?.split("\n")[0]?.trim();
    if (!raw) continue;
    if (/^0x[0-9a-f]+$/i.test(raw)) continue;
    if (
      /^(execution reverted|unknown reason|missing revert data|error)$/i.test(raw)
    ) {
      continue;
    }
    return raw.replace(/[.'"]+$/, "").trim();
  }
  return null;
};

const shortUnknownDetail = (text: string): string => {
  const line =
    text
      .split("\n")
      .map((entry) => entry.trim())
      .find(
        (entry) =>
          entry &&
          !/^version:/i.test(entry) &&
          !/^docs:/i.test(entry) &&
          !/^contract call:/i.test(entry) &&
          !/^request arguments:/i.test(entry) &&
          !/^https?:\/\//i.test(entry)
      ) || "";

  if (!line) return "";
  if (line.length > 140) return `${line.slice(0, 137)}...`;
  return line;
};

const revertedMessage = (reason: string): string => {
  const customName = /^[A-Za-z_][A-Za-z0-9_]*$/.test(reason) ? reason : null;
  if (customName && CONTRACT_ERROR_MESSAGES[customName]) {
    return CONTRACT_ERROR_MESSAGES[customName];
  }

  for (const entry of ERC_REQUIRE_MESSAGES) {
    if (entry.test.test(reason)) return entry.message;
  }

  if (customName) {
    return `This action was rejected: ${humanizeErrorName(customName)}.`;
  }

  return `This action was rejected: ${reason}.`;
};

export const parseTxError = (
  error: unknown,
  fallback?: ErrorTemplate
): ParsedTxError => {
  const text = collectErrorText(error);
  const name =
    error && typeof error === "object" && "name" in error
      ? String((error as { name?: string }).name || "")
      : "";

  if (
    name === "UserRejectedRequestError" ||
    matches(text, /user rejected/i) ||
    matches(text, /user denied/i) ||
    matches(text, /rejected the request/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.REJECTED, kind: "rejected" };
  }

  if (matches(text, /transaction indexing is in progress/i)) {
    return {
      code: "INDEXING",
      message: "Transaction indexing is in progress.",
      kind: "indexing",
    };
  }

  if (
    name === "ChainMismatchError" ||
    name === "SwitchChainError" ||
    matches(text, /chain mismatch/i) ||
    matches(text, /does not match the (target )?chain/i) ||
    matches(text, /wrong network/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.WRONG_NETWORK, kind: "wrong-network" };
  }

  if (
    matches(text, /nonce too (low|high)/i) ||
    matches(text, /already pending/i) ||
    matches(text, /replacement (transaction )?underpriced/i) ||
    matches(text, /replacement fee too low/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.NONCE, kind: "nonce" };
  }

  if (
    matches(text, /out of gas/i) ||
    matches(text, /intrinsic gas too low/i) ||
    matches(text, /exceeds (the )?(configured )?block gas limit/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.OUT_OF_GAS, kind: "out-of-gas" };
  }

  const customErrorName = extractCustomErrorName(text);
  if (customErrorName) {
    return {
      ...Errors.GLOBAL.WEB3.CONTRACT_REVERTED,
      kind: "reverted",
      params: { reason: revertedMessage(customErrorName) },
    };
  }

  const serializedCode =
    error && typeof error === "object" && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  if (typeof serializedCode === "string" && isCustomErrorCode(serializedCode)) {
    return {
      ...Errors.GLOBAL.WEB3.CONTRACT_REVERTED,
      kind: "reverted",
      params: { reason: revertedMessage(serializedCode) },
    };
  }

  const selector = extractErrorSelector(text);
  if (selector && ERROR_SELECTOR_TO_NAME[selector]) {
    return {
      ...Errors.GLOBAL.WEB3.CONTRACT_REVERTED,
      kind: "reverted",
      params: { reason: revertedMessage(ERROR_SELECTOR_TO_NAME[selector]) },
    };
  }

  const revertReason = extractRevertReason(text);
  if (revertReason) {
    return {
      ...Errors.GLOBAL.WEB3.CONTRACT_REVERTED,
      kind: "reverted",
      params: { reason: revertedMessage(revertReason) },
    };
  }

  if (
    name === "ContractFunctionExecutionError" ||
    name === "ContractFunctionRevertedError" ||
    name === "EstimateGasExecutionError" ||
    name === "CallExecutionError" ||
    matches(text, /execution reverted/i) ||
    matches(text, /the contract function .* reverted/i)
  ) {
    return {
      ...Errors.GLOBAL.WEB3.CONTRACT_REVERTED,
      kind: "reverted",
      params: {
        reason: "The contract rejected this action without a specific reason.",
      },
    };
  }

  if (
    name === "InsufficientFundsError" ||
    matches(text, /insufficient funds for (gas|intrinsic)/i) ||
    matches(text, /insufficient funds/i) ||
    matches(text, /exceeds the balance of the account/i)
  ) {
    return {
      ...Errors.GLOBAL.WEB3.INSUFFICIENT_FUNDS,
      kind: "insufficient-funds",
      params: { token: config.chainSymbol },
    };
  }

  if (
    name === "HttpRequestError" ||
    name === "TimeoutError" ||
    name === "RpcRequestError" ||
    matches(text, /failed to fetch/i) ||
    matches(text, /network error/i) ||
    matches(text, /timed? ?out/i) ||
    matches(text, /internal json-rpc error/i) ||
    matches(text, /could not coalesce error/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.NETWORK, kind: "network" };
  }

  if (
    /^transaction reverted$/i.test(text.trim()) ||
    matches(text, /transaction reverted/i)
  ) {
    return { ...Errors.GLOBAL.WEB3.TX_REVERTED, kind: "tx-reverted" };
  }

  if (fallback) {
    return { ...fallback, kind: "unknown" };
  }

  const detail = shortUnknownDetail(text);
  return {
    code: Errors.GLOBAL.WEB3.UNKNOWN.code,
    message: detail
      ? `${Errors.GLOBAL.WEB3.UNKNOWN.message} ${detail}`
      : Errors.GLOBAL.WEB3.UNKNOWN.message,
    kind: "unknown",
  };
};

export const isUserRejectedTxError = (error: unknown) =>
  parseTxError(error).kind === "rejected";

export const isIndexingTxError = (error: unknown) =>
  parseTxError(error).kind === "indexing";

export const toastTxError = (error: unknown, fallback?: ErrorTemplate) => {
  const parsed = parseTxError(error, fallback);
  if (parsed.kind === "indexing") return parsed;
  toastError(parsed, parsed.params);
  return parsed;
};
